import { useImperativeHandle, useRef, forwardRef, useEffect } from 'react'
import { makeStyles } from '@material-ui/core/styles'

const useStyles = makeStyles((theme) => ({
  textarea: {
    flex: 1,
    width: '100%',
    padding: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.grey[400]}`,
    fontSize: '1.4rem',
    fontFamily: 'Arial, sans-serif',
    resize: 'none',
    overflow: 'hidden',
    lineHeight: 1.5,
    paddingRight: theme.spacing(6),
  },
}))

export type PromptTextAreaHandle = {
  getValue: () => string
  clear: () => void
}

interface PromptTextAreaProps {
  disabled: boolean
  value: string
  onChange: (value: string) => void
  onEnterPress: () => void
  placeholder?: string
}

const PromptTextArea = forwardRef<PromptTextAreaHandle, PromptTextAreaProps>(
  ({ value, onChange, onEnterPress, disabled, placeholder = 'Chat with Plus...' }, ref) => {
    const classes = useStyles()
    const textareaRef = useRef<HTMLTextAreaElement>(null)

    const adjustHeight = () => {
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto'
        textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`
      }
    }

    useEffect(() => {
      adjustHeight()
    }, [value])

    useImperativeHandle(ref, () => ({
      getValue: () => textareaRef.current?.value || '',
      clear: () => {
        onChange('')

        if (textareaRef.current) {
          textareaRef.current.style.height = 'auto'
          textareaRef.current.focus()
        }
      },
      focus: () => {
        textareaRef.current?.focus()
      },
    }))

    const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault()
        onEnterPress()
      }
    }

    const handleInputChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      onChange(event.target.value)
    }

    return (
      <textarea
        ref={textareaRef}
        value={value}
        rows={1}
        placeholder={placeholder}
        className={classes.textarea}
        onChange={handleInputChange}
        onInput={adjustHeight}
        onKeyDown={handleKeyDown}
        disabled={disabled}
      />
    )
  }
)

export default PromptTextArea
